// src/pages/qbo/ReviewPushPanel.tsx
//
// The review step every QBO sync goes through (3.17.0): pick a date range,
// look at every record in it with its sync state and warnings, tick the ones
// to send, push. Nothing reaches QuickBooks until "Push selected" is
// confirmed, and the operator can leave records out (e.g. a receipt whose
// deposit date still has to be corrected in Advantage).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react';
import DateField from '../../components/DateField';
import qboApi from '../../services/qboApi';
import SyncResultList from './SyncResultList';
import type { ReviewResult, ReviewRow, SyncResult } from './qboTypes';
import { isoDaysAgo, isoToday, money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';

export type ReviewEntity = 'customers' | 'invoices' | 'payments' | 'purchases' | 'vendors';

interface Props {
  entity: ReviewEntity;
  label: string;                 // "Invoices"
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
  onPushed?: () => void;         // refresh summaries
  minFrom?: string | null;       // earliest pushable date (day after the conversion cutoff); the From date is clamped to it
}

interface Col {
  key: string;
  label: string;
  align?: 'right';
  render: (r: ReviewRow) => ReactNode;
}

const fmtDate = (s?: string | null) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString() : '');

// 3.19: a receipt that applies more than a document is worth. The agent marks it
// in the review and REFUSES it on push; the operator has to acknowledge the
// mismatch here before it is posted capped at the QuickBooks balances.
const OVER_APPLIED = 'OVER-APPLIED';
const isOverApplied = (r: ReviewRow) => r.warnings.some((w) => w.startsWith(OVER_APPLIED));

function columnsFor(entity: ReviewEntity): Col[] {
  const key: Col = { key: 'key', label: entity === 'payments' ? 'Receipt#' : entity === 'purchases' ? 'PO#' : entity === 'invoices' ? 'Doc#' : 'Code', render: (r) => <span className="font-mono text-xs">{r.key}</span> };
  const name: Col = { key: 'name', label: entity === 'purchases' || entity === 'vendors' ? 'Vendor' : 'Customer', render: (r) => <span title={`code ${r.code}`}>{r.name}</span> };
  const status: Col = {
    key: 'status', label: 'Status', render: (r) => (
      r.type === 'Deleted'
        ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-900/60 text-red-300" title={r.warnings.join('\n')}>Deleted in ADS{r.qboId ? ` #${r.qboId}` : ''}</span>
        : r.voided
          ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-900/60 text-red-300" title={r.warnings.join('\n')}>Voided{r.qboId ? ` #${r.qboId}` : ''}</span>
          : r.modified
            ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-900/60 text-amber-300" title={r.warnings.join('\n')}>Changed{r.qboId ? ` #${r.qboId}` : ''}</span>
      : r.alreadySynced
        ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-900/60 text-green-300" title={r.syncAction || ''}>{r.syncAction?.startsWith('Convert') ? 'Converted' : 'Synced'}{r.qboId ? ` #${r.qboId}` : ''}</span>
        : r.lastError
          ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-900/60 text-red-300" title={r.lastError}>{r.syncStatus || 'Error'}</span>
          : <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-900/60 text-blue-300">New</span>
    ),
  };
  const linked: Col = { key: 'linked', label: entity === 'purchases' ? 'Vend' : 'Cust', render: (r) => r.customerLinked ? <CheckCircle2 size={14} className="text-green-400" /> : <XCircle size={14} className="text-amber-400" /> };
  const money$ = (v: number | null | undefined) => <span className="font-mono text-xs">{money(v)}</span>;

  switch (entity) {
    case 'invoices':
      return [
        key,
        { key: 'type', label: 'Type', render: (r) => <span className={r.type === 'CreditMemo' ? 'text-amber-300' : 'text-slate-300'}>{r.type === 'CreditMemo' ? 'Credit' : 'Invoice'}</span> },
        { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
        name,
        { key: 'amount', label: 'Total', align: 'right', render: (r) => money$(r.amount) },
        { key: 'balance', label: 'Balance', align: 'right', render: (r) => money$(r.balance) },
        { key: 'cost', label: 'Cost (ADS)', align: 'right', render: (r) => money$(r.cost) },
        { key: 'ref', label: 'Terms', render: (r) => r.ref || '' },
        linked, status,
      ];
    case 'payments':
      return [
        // 3.19: key = receipt:customer (a reversal / credit application can share a number with another customer's receipt)
        { key: 'key', label: 'Receipt', render: (r) => <span className="font-mono text-xs" title={r.key}>{r.key.split(':')[0]}</span> },
        { key: 'type', label: 'Kind', render: (r) => r.type === 'CreditApply' ? <span className="text-amber-300" title="zero-amount receipt: credit memos pay the invoices, no cash">credit</span> : r.type === 'Reversal' ? <span className="text-red-300" title="bounced check / reversal → journal">bounce</span> : r.ref === 'CREDIT' ? <span className="text-slate-300" title="paid by credit card">card</span> : <span className="text-slate-300">payment</span> },
        { key: 'date', label: 'Pay date', render: (r) => fmtDate(r.date) },
        { key: 'date2', label: 'Cash date', render: (r) => fmtDate(r.date2) },
        { key: 'date3', label: 'Deposited', render: (r) => r.date3 ? fmtDate(r.date3) : <span className="text-amber-400">—</span> },
        name,
        { key: 'amount', label: 'Amount', align: 'right', render: (r) => money$(r.amount) },
        { key: 'balance', label: 'Applied', align: 'right', render: (r) => money$(r.balance) },
        { key: 'ref', label: 'Check / Ref', render: (r) => <span className="font-mono text-xs">{r.ref || ''}</span> },
        { key: 'lines', label: 'Docs', render: (r) => r.lines },
        linked, status,
      ];
    case 'purchases':
      return [
        key,
        { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
        { key: 'date2', label: 'Due', render: (r) => fmtDate(r.date2) },
        name,
        { key: 'amount', label: 'Total', align: 'right', render: (r) => money$(r.amount) },
        linked, status,
      ];
    case 'customers':
      return [
        key,
        { key: 'date', label: 'Entered', render: (r) => fmtDate(r.date) },
        name,
        { key: 'ref', label: 'City', render: (r) => r.ref || '' },
        { key: 'amount', label: 'Balance', align: 'right', render: (r) => money$(r.amount) },
        status,
      ];
    case 'vendors':
    default:
      return [key, name, { key: 'ref', label: 'City', render: (r) => r.ref || '' }, status];
  }
}

export default function ReviewPushPanel({ entity, label, onMessage, onPushed, minFrom }: Props) {
  const [from, setFrom] = useState(isoDaysAgo(7));
  const [to, setTo] = useState(isoToday());

  // Everything on/before the conversion cutoff is already in QuickBooks as the
  // legacy summary journals (open documents go through Conversion), so the
  // agent hides it and the panel never offers a range that starts before it.
  useEffect(() => {
    if (minFrom && from < minFrom) setFrom(minFrom);
  }, [minFrom]); // eslint-disable-line react-hooks/exhaustive-deps
  const [review, setReview] = useState<ReviewResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [hideSynced, setHideSynced] = useState(true);   // rows with an error or never pushed stay visible
  const [filter, setFilter] = useState('');
  // Receipts the last push refused as over-applied: { key → agent message }. Shown as a dialog the operator must answer.
  const [overApplied, setOverApplied] = useState<{ key: string; error: string }[]>([]);
  const [capSelected, setCapSelected] = useState<Set<string>>(new Set());
  const [confirmingCap, setConfirmingCap] = useState(false);
  // Safe to post capped = every line says the customer balance stays equal; a receipt where Advantage dropped the
  // excess or over-used a credit is better corrected at the source, so it starts unticked.
  const capSafe = (error: string) => !/DROPPED|differs by/.test(error);

  const cols = useMemo(() => columnsFor(entity), [entity]);

  const visible = useMemo(() => {
    if (!review) return [];
    const f = filter.trim().toLowerCase();
    return review.rows.filter((r) =>
      (!hideSynced || !r.alreadySynced || r.modified || r.voided) &&
      (!f || r.key.toLowerCase().includes(f) || r.name.toLowerCase().includes(f) || String(r.code).includes(f) || (r.ref || '').toLowerCase().includes(f)),
    );
  }, [review, hideSynced, filter]);

  const selectedRows = useMemo(() => (review ? review.rows.filter((r) => selected.has(r.key)) : []), [review, selected]);
  const selectedTotal = selectedRows.reduce((s, r) => s + (r.amount || 0), 0);

  const load = async () => {
    if (!from || !to) return;
    setLoading(true);
    setResult(null);
    setConfirming(false);
    setOverApplied([]);
    try {
      const r = (await qboApi.review(entity, from, to)) as ReviewResult;
      setReview(r);
      // Default selection: everything not yet synced, plus rows changed / deleted in Advantage since their push (3.19.4).
      setSelected(new Set(r.rows.filter((x) => !x.alreadySynced || x.modified).map((x) => x.key)));
    } catch (e: unknown) {
      onMessage('error', (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const selectVisible = (only: 'all' | 'new' | 'changed' | 'none') => {
    setSelected(() => {
      if (only === 'none') return new Set();
      if (only === 'changed') return new Set(visible.filter((r) => r.modified || r.voided).map((r) => r.key));
      return new Set(visible.filter((r) => only === 'all' || !r.alreadySynced || r.modified).map((r) => r.key));
    });
  };
  const changedCount = review ? review.rows.filter((r) => r.modified || r.voided).length : 0;

  const detailKey = (d: { invoice?: string; receipt?: string; poNo?: string; code?: string }) => String(d.invoice || d.receipt || d.poNo || d.code || '');

  const pushKeys = async (keys: string[], allowCap: boolean) => {
    setPushing(true);
    setResult(null);
    setJob(null);
    setOverApplied([]);
    try {
      const r = await runJob(() => qboApi.push(entity, keys, { allowCap }), setJob);
      setResult(r);
      const errs = (r.details || []).filter((d) => d.status === 'error');
      // 3.19: receipts the agent refused because Advantage applied more than a document is worth.
      const over = errs.filter((d) => (d.error || '').startsWith(OVER_APPLIED)).map((d) => ({ key: detailKey(d), error: d.error || '' }));
      setOverApplied(over);
      setCapSelected(new Set(over.filter((o) => capSafe(o.error)).map((o) => o.key)));
      const summary = `${label}: ${r.created} created, ${r.updated} updated, ${r.skipped} skipped, ${r.errors} errors`;
      onMessage(r.errors ? 'error' : 'success', over.length ? `${summary} — ${over.length} over-applied receipt(s) need your OK below` : summary);
      onPushed?.();
      // Refresh the review so statuses update, keep the failed ones selected.
      const again = (await qboApi.review(entity, from, to)) as ReviewResult;
      setReview(again);
      const failed = new Set(errs.map(detailKey));
      setSelected(new Set(again.rows.filter((x) => failed.has(x.key)).map((x) => x.key)));
    } catch (e: unknown) {
      onMessage('error', (e as Error).message);
    } finally {
      setPushing(false);
      setJob(null);
    }
  };

  const push = async () => {
    if (selected.size === 0) return;
    if (!confirming) { setConfirming(true); setTimeout(() => setConfirming(false), 6000); return; }
    setConfirming(false);
    await pushKeys(Array.from(selected), false);
  };

  // The operator read the mismatch and clicked OK: post these receipts capped at the QuickBooks balances.
  const pushCapped = async () => {
    const keys = overApplied.filter((o) => capSelected.has(o.key)).map((o) => o.key);
    if (keys.length === 0) return;
    if (!confirmingCap) { setConfirmingCap(true); setTimeout(() => setConfirmingCap(false), 8000); return; }
    setConfirmingCap(false);
    await pushKeys(keys, true);
  };
  const toggleCap = (k: string) => setCapSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const noDates = entity === 'vendors';

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5">
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="text-sm font-semibold text-slate-400 mr-auto">
          Review &amp; push {label.toLowerCase()}
          <div className="text-[11px] font-normal text-slate-500 mt-0.5">
            {noDates ? 'All active vendors.' : `By ${entity === 'payments' ? 'payment date' : entity === 'customers' ? 'date entered' : 'document date'}, inclusive. Nothing is sent until you push.`}
          </div>
        </div>
        {!noDates && (
          <>
            <DateField label="From" value={from} onChange={(v) => setFrom(minFrom && v && v < minFrom ? minFrom : v)} min={minFrom || undefined} max={to || undefined} />
            <DateField label="To" value={to} onChange={setTo} min={from || undefined} />
          </>
        )}
        <button
          className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
          onClick={load}
          disabled={loading || (!noDates && (!from || !to))}
        >
          {loading ? 'Loading…' : review ? 'Reload' : 'Load'}
        </button>
      </div>

      {review && (
        <>
          {review.notes.length > 0 && (
            <ul className="mb-3 text-[11px] text-slate-400 space-y-0.5">
              {review.notes.map((n, i) => <li key={i}>• {n}</li>)}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
            <span className="text-slate-400">
              {review.count} records · {review.alreadySynced} already in QuickBooks
              {review.total ? <> · total <span className="font-mono">${money(review.total)}</span></> : null}
            </span>
            <span className="mx-1 text-slate-600">|</span>
            <button className="text-blue-300 hover:underline" onClick={() => selectVisible('new')}>select new</button>
            {changedCount > 0 && <button className="text-amber-300 hover:underline" onClick={() => selectVisible('changed')}>select changed ({changedCount})</button>}
            <button className="text-blue-300 hover:underline" onClick={() => selectVisible('all')}>select all shown</button>
            <button className="text-blue-300 hover:underline" onClick={() => selectVisible('none')}>clear</button>
            <label className="ml-2 flex items-center gap-1 text-slate-400 cursor-pointer">
              <input type="checkbox" checked={hideSynced} onChange={(e) => setHideSynced(e.target.checked)} /> hide synced
            </label>
            <input
              className="ml-auto px-2 py-1 rounded border border-slate-600 bg-slate-900 text-slate-200 text-xs w-44 focus:outline-none focus:border-blue-500"
              placeholder="filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          <div className="overflow-x-auto max-h-[520px] overflow-y-auto rounded-lg border border-slate-700/60">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-slate-800">
                <tr>
                  <th className="px-2 py-2 border-b border-slate-700 w-8">
                    <input
                      type="checkbox"
                      checked={visible.length > 0 && visible.every((r) => selected.has(r.key))}
                      onChange={(e) => selectVisible(e.target.checked ? 'all' : 'none')}
                      title="select / clear all shown"
                    />
                  </th>
                  {cols.map((c) => (
                    <th key={c.key} className={`px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                  ))}
                  <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Notes</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={cols.length + 2} className="px-3 py-4 text-slate-500 text-sm">No records</td></tr>
                )}
                {visible.map((r) => (
                  <tr key={r.key} className={`hover:bg-slate-700/30 ${selected.has(r.key) ? 'bg-blue-950/30' : ''}`}>
                    <td className="px-2 py-1.5 border-b border-slate-800/60"><input type="checkbox" checked={selected.has(r.key)} onChange={() => toggle(r.key)} /></td>
                    {cols.map((c) => (
                      <td key={c.key} className={`px-3 py-1.5 border-b border-slate-800/60 text-slate-300 whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}>{c.render(r)}</td>
                    ))}
                    <td className={`px-3 py-1.5 border-b border-slate-800/60 text-[11px] max-w-[360px] ${isOverApplied(r) ? 'text-red-300 font-medium' : 'text-amber-300'}`}>
                      {r.warnings.length > 0 && (
                        <span className="inline-flex items-start gap-1" title={r.warnings.join('\n')}>
                          {isOverApplied(r) ? <ShieldAlert size={12} className="mt-0.5 shrink-0" /> : <AlertTriangle size={12} className="mt-0.5 shrink-0" />}
                          <span className="truncate">{r.warnings.join(' · ')}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className="text-xs text-slate-400">
              {selected.size} selected{selectedTotal ? <> · <span className="font-mono">${money(selectedTotal)}</span></> : null}
            </span>
            {pushing && job && (
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <div className="w-40 h-2 rounded bg-slate-700 overflow-hidden"><div className="h-2 bg-blue-500 transition-all" style={{ width: `${job.total ? Math.round((100 * job.done) / job.total) : 0}%` }} /></div>
                <span className="font-mono">{job.done}/{job.total}</span>
                <button className="text-red-300 hover:underline" onClick={() => qboApi.cancelJob(job.jobId).catch(() => undefined)}>cancel</button>
              </div>
            )}
            <button
              className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${confirming ? 'bg-amber-600 hover:bg-amber-500' : 'bg-green-600 hover:bg-green-500'}`}
              onClick={push}
              disabled={pushing || selected.size === 0}
            >
              {pushing ? 'Pushing…' : confirming ? `Confirm: push ${selected.size} to QuickBooks` : `Push selected (${selected.size})`}
            </button>
          </div>

          {overApplied.length > 0 && !pushing && (
            <div className="mt-3 rounded-lg border border-red-700/70 bg-red-950/40 p-4" role="alertdialog" aria-labelledby="over-applied-title">
              <div id="over-applied-title" className="flex items-center gap-2 text-sm font-semibold text-red-200">
                <ShieldAlert size={16} />
                {overApplied.length} receipt{overApplied.length === 1 ? '' : 's'} not pushed: Advantage applies more than the document is worth
              </div>
              <p className="mt-1 text-xs text-red-200/90">
                QuickBooks will not accept these as entered. The right fix is in Advantage: correct the receipt's applications, then push again.
                If you post them capped instead, each line is applied only up to what is open in QuickBooks, the rest of the invoice stays open there,
                and the customer's balance in Advantage is off by the difference until the receipt is corrected.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-red-100 max-h-64 overflow-y-auto">
                {overApplied.map((o) => (
                  <li key={o.key} className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-0.5 shrink-0" checked={capSelected.has(o.key)} onChange={() => toggleCap(o.key)} title={capSafe(o.error) ? 'balances stay equal after a capped post' : 'better corrected in Advantage first'} />
                    <span className="font-mono shrink-0">{o.key.split(':')[0]}</span>
                    <span className={capSafe(o.error) ? '' : 'text-amber-200'}>{o.error.replace(/^OVER-APPLIED:\s*/, '')}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] text-red-200/80">Ticked by default: receipts whose customer balance stays equal after a capped post. Unticked: Advantage dropped the excess or over-used a credit, fix those in Advantage first.</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors"
                  onClick={() => { setOverApplied([]); setConfirmingCap(false); }}
                >
                  Fix in Advantage first (dismiss)
                </button>
                <button
                  className={`ml-auto px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors disabled:opacity-50 ${confirmingCap ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-700 hover:bg-amber-600'}`}
                  onClick={pushCapped}
                  disabled={capSelected.size === 0}
                >
                  {confirmingCap ? `OK, I understand — post ${capSelected.size} capped` : `Post capped at QuickBooks balances (${capSelected.size})`}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <SyncResultList result={result} title={`${label} push`} />
    </div>
  );
}
